/* eslint-disable react/jsx-wrap-multilines */
import { useState, useRef, useEffect } from 'react';
import {
  Drawer,
  DrawerTrigger,
  DrawerCloseButton,
  DrawerHeader,
  Flex,
  DrawerBody,
  DrawerFooter,
  Button,
  Text,
  Heading,
  Box,
  Dropdown,
  DropdownItem,
} from '@grupoboticario/flora-react';
import { format } from 'date-fns';

import { FiFilter } from 'react-icons/fi';

import Calendar from 'react-calendar';
import { CalendarWrap } from './stylesCalendar';
import {
  FilterButton,
  StyledDrawerContent,
  ClearFilterButton,
  CalendarIcon,
  CalendarInput,
  CalendarRef,
} from './styles';

interface FilterDrawerProps {
  onApplyFilter?: (filters: any) => void;
  onClearFilter?: () => void;
  hasExpirationDate?: boolean;
  franchises?: any;
}

export function FilterDrawer({
  onApplyFilter,
  onClearFilter,
  franchises,
}: Readonly<FilterDrawerProps>) {
  const ref = useRef<any>();
  const [drawerIsOpen, setDrawerIsOpen] = useState(false);
  const [appliedFilterCount, setAppliedFilterCount] = useState(0);

  const [selectedFranchises, setSelectedFranchises] = useState<string[]>([]);
  const [period, setPeriod] = useState('');
  const [startDate, setStartDate] = useState<Date>(null);
  const [endDate, setEndDate] = useState<Date>(null);
  const [rangeDate, setRangeDate] = useState<Date | [Date, Date]>(null);
  const [valueInput, setValueInput] = useState('');

  const [showCalendar, setShowCalendar] = useState(false);

  const formatDate = (date: any) => {
    const newDate = new Date(date);
    const day = newDate.toLocaleString('pt-BR', { day: '2-digit' });
    const month = newDate.toLocaleString('pt-BR', { month: '2-digit' });
    const year = newDate.toLocaleString('pt-BR', { year: 'numeric' });
    const newDateString = `${day}/${month}/${year}`;
    return newDateString;
  };

  function toggleDrawer() {
    setDrawerIsOpen(!drawerIsOpen);
  }
  function handleApplyFilter() {
    setAppliedFilterCount(0);

    onApplyFilter({
      selectedFranchises,
      start: startDate ? format(startDate, 'yyyy-MM-dd') : '',
      end: endDate ? format(endDate, 'yyyy-MM-dd') : '',
    });
    [period, startDate, selectedFranchises.length].forEach(applied => {
      if (applied) {
        setAppliedFilterCount(prev => prev + 1);
      }
    });

    toggleDrawer();
  }

  function handleDateChange(value: any) {
    if (value) {
      setStartDate(value[0]);
      setEndDate(value[1]);

      setRangeDate(value);
    }
    setShowCalendar(false);
  }

  useEffect(() => {
    if (rangeDate) {
      setValueInput(
        `${formatDate(rangeDate[0])} à ${formatDate(rangeDate[1])}`,
      );
    }
  }, [rangeDate]);

  function handleClearFilter() {
    onClearFilter();
    setPeriod('');
    setSelectedFranchises([]);
    setAppliedFilterCount(0);
    setStartDate(null);
    setEndDate(null);
    setRangeDate([new Date(), new Date()]);
    setValueInput('');

    toggleDrawer();
  }

  const handleClickOutside = (event: any) => {
    if (ref.current && !ref.current.contains(event.target)) {
      setShowCalendar(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Drawer open={drawerIsOpen} onOpenChange={toggleDrawer}>
      <DrawerTrigger>
        <FilterButton hierarchy="secondary" has="iconRight" icon={<FiFilter />}>
          <Text>
            Filtrar
            {appliedFilterCount > 0 && (
              <Text as="sup">{appliedFilterCount}</Text>
            )}
          </Text>
        </FilterButton>
      </DrawerTrigger>
      <StyledDrawerContent side="right">
        <DrawerHeader>
          <DrawerCloseButton />
          <Flex
            align="center"
            justify="space-between"
            css={{ marginTop: '24px' }}
          >
            <Heading level={5}>Filtrar por:</Heading>
            <ClearFilterButton
              styleSemantic="primary"
              size="bodyLargeStandardRegular"
              onClick={handleClearFilter}
            >
              Limpar filtros
            </ClearFilterButton>
          </Flex>
        </DrawerHeader>
        <DrawerBody>
          <Box>
            <Text weight="heavy">Lojas:</Text>
            <Dropdown
              id="franchises-dropdown"
              labelText="Selecione as lojas desejadas"
              optionalText=""
              value={selectedFranchises}
              onChange={event => setSelectedFranchises(event.target.value)}
              renderValue={selected => selected.join(', ')}
              multiple
            >
              {franchises?.map(franchise => (
                <DropdownItem key={franchise.code} value={franchise.code}>
                  <Text isTruncated>
                    {`${franchise.code} - ${franchise.name}`}
                  </Text>
                </DropdownItem>
              ))}
            </Dropdown>
          </Box>
          <Box>
            <Text weight="heavy"> Período:</Text>
          </Box>
          <Box>
            <CalendarRef ref={ref}>
              <CalendarInput onClick={() => setShowCalendar(!showCalendar)}>
                <input
                  id="inputDate"
                  value={valueInput}
                  onChange={() => null}
                  disabled
                  placeholder="Selecione um período"
                />
                <CalendarIcon />
              </CalendarInput>

              {showCalendar && (
                <CalendarWrap>
                  <Calendar
                    onChange={(value: any) => handleDateChange(value)}
                    value={rangeDate}
                    selectRange
                  />
                </CalendarWrap>
              )}
            </CalendarRef>
          </Box>
        </DrawerBody>
        <DrawerFooter>
          <Flex gapX="$5">
            <DrawerCloseButton asChild>
              <Button hierarchy="secondary">Fechar</Button>
            </DrawerCloseButton>
            <Button
              size="medium"
              styleSemantic="neutral"
              onClick={handleApplyFilter}
            >
              Aplicar filtros
            </Button>
          </Flex>
        </DrawerFooter>
      </StyledDrawerContent>
    </Drawer>
  );
}
